import { driver } from 'driver.js';
import 'driver.js/dist/driver.css';

const TOUR_DONE_KEY = 'looperc_tour_done';

const LANDING_HTML = `
<p style="text-align:center;font-size:1.1em;font-weight:600;margin:0 0 18px;line-height:1.4;">
  Community-powered drum loops.<br>Hit play. Start jamming.
</p>
<div style="display:flex;flex-direction:column;gap:9px;margin-bottom:18px;">
  <div style="display:flex;align-items:flex-start;gap:10px;">
    <span style="width:4px;height:4px;border-radius:50%;background:linear-gradient(135deg,#7fd3ff,#9dffb3);flex-shrink:0;margin-top:9px;"></span>
    <span>Grab your guitar. <strong>Your drum loop is ready.</strong></span>
  </div>
  <div style="display:flex;align-items:flex-start;gap:10px;">
    <span style="width:4px;height:4px;border-radius:50%;background:linear-gradient(135deg,#7fd3ff,#9dffb3);flex-shrink:0;margin-top:9px;"></span>
    <span>Practice that lesson inside <strong>a real groove</strong>, not a metronome click</span>
  </div>
  <div style="display:flex;align-items:flex-start;gap:10px;">
    <span style="width:4px;height:4px;border-radius:50%;background:linear-gradient(135deg,#7fd3ff,#9dffb3);flex-shrink:0;margin-top:9px;"></span>
    <span>Upload your loop samples. <strong>Let musicians like you jam over them.</strong></span>
  </div>
  <div style="display:flex;align-items:flex-start;gap:10px;">
    <span style="width:4px;height:4px;border-radius:50%;background:linear-gradient(135deg,#7fd3ff,#9dffb3);flex-shrink:0;margin-top:9px;"></span>
    <span><strong>No DAW. No setup.</strong> Browser. Done.</span>
  </div>
</div>
<div style="border-top:1px solid rgba(128,128,128,0.15);padding-top:12px;">
  <p style="margin:0 0 10px;font-size:0.82em;opacity:0.6;text-align:center;line-height:1.4;">
    Loop samples uploaded by musicians, for musicians.
  </p>
  <label id="tourDontShowLabel" style="display:flex;align-items:center;gap:8px;cursor:pointer;font-size:0.85em;opacity:0.55;user-select:none;margin-bottom:10px;">
    <input type="checkbox" id="tourDontShowCheckbox" style="width:14px;height:14px;cursor:pointer;accent-color:#7fd3ff;" />
    Don't show again
  </label>
  <p style="margin:0;font-size:0.72em;opacity:0.35;line-height:1.4;">
    We use a strictly necessary session cookie to keep you logged in. No tracking cookies.
  </p>
</div>
`;

const STYLE_GROUPS_HTML = `
<p style="margin:0 0 12px;">Tap <strong>← →</strong> to browse drum styles.</p>
<div style="display:grid;grid-template-columns:1fr 1fr;gap:8px;margin-bottom:12px;">
  <div style="padding:8px 10px;border-radius:8px;background:rgba(128,200,128,0.08);border:1px solid rgba(128,200,128,0.2);">
    <div style="font-weight:600;font-size:0.87em;">Organic</div>
    <div style="font-size:0.74em;opacity:0.6;">Jazz · Blues · Acoustic</div>
  </div>
  <div style="padding:8px 10px;border-radius:8px;background:rgba(200,160,80,0.08);border:1px solid rgba(200,160,80,0.2);">
    <div style="font-weight:600;font-size:0.87em;">Groove</div>
    <div style="font-size:0.74em;opacity:0.6;">Funk · Reggae · Soul</div>
  </div>
  <div style="padding:8px 10px;border-radius:8px;background:rgba(200,80,80,0.08);border:1px solid rgba(200,80,80,0.2);">
    <div style="font-weight:600;font-size:0.87em;">Rock</div>
    <div style="font-size:0.74em;opacity:0.6;">Country · Garage · Hard</div>
  </div>
  <div style="padding:8px 10px;border-radius:8px;background:rgba(100,100,220,0.08);border:1px solid rgba(100,100,220,0.2);">
    <div style="font-weight:600;font-size:0.87em;">Beat</div>
    <div style="font-size:0.74em;opacity:0.6;">Hip-Hop · EDM · Trap</div>
  </div>
</div>
<p style="margin:0;font-size:0.82em;opacity:0.6;">Each style draws from a community pool of real loop samples.</p>
`;

const COMPLEXITY_HTML = `
<p style="margin:0 0 12px;">Choose how your loop is built:</p>
<div style="display:flex;flex-direction:column;gap:9px;">
  <div style="display:flex;gap:10px;align-items:baseline;">
    <span style="font-family:'Orbitron',sans-serif;font-size:0.7em;font-weight:700;color:#7fd3ff;min-width:72px;flex-shrink:0;letter-spacing:0.04em;">STANDARD</span>
    <span style="font-size:0.88em;opacity:0.8;">Tight, curated patterns</span>
  </div>
  <div style="display:flex;gap:10px;align-items:baseline;">
    <span style="font-family:'Orbitron',sans-serif;font-size:0.7em;font-weight:700;color:#9dffb3;min-width:72px;flex-shrink:0;letter-spacing:0.04em;">CREATIVE</span>
    <span style="font-size:0.88em;opacity:0.8;">Longer, varied structure</span>
  </div>
  <div style="display:flex;gap:10px;align-items:baseline;">
    <span style="font-family:'Orbitron',sans-serif;font-size:0.7em;font-weight:700;color:#ffb3c6;min-width:72px;flex-shrink:0;letter-spacing:0.04em;">CRAZY</span>
    <span style="font-size:0.88em;opacity:0.8;">Unpredictable — each section goes its own way</span>
  </div>
  <div style="display:flex;gap:10px;align-items:baseline;">
    <span style="font-family:'Orbitron',sans-serif;font-size:0.7em;font-weight:700;color:#ffd37f;min-width:72px;flex-shrink:0;letter-spacing:0.04em;">CUSTOM</span>
    <span style="font-size:0.88em;opacity:0.8;">You design every section manually</span>
  </div>
</div>
`;

const SEQUENCE_STRIP_HTML = `
<div style="display:flex;gap:4px;margin:12px 0 8px;flex-wrap:nowrap;overflow:hidden;">
  <div style="background:rgba(99,102,241,0.25);border:1px solid rgba(99,102,241,0.5);border-radius:6px;padding:5px 10px;font-size:0.76em;color:#a5b4fc;white-space:nowrap;font-family:'Orbitron',sans-serif;">Intro 4</div>
  <div style="background:rgba(59,130,246,0.25);border:1px solid rgba(59,130,246,0.5);border-radius:6px;padding:5px 10px;font-size:0.76em;color:#93c5fd;white-space:nowrap;font-family:'Orbitron',sans-serif;">Vs 8</div>
  <div style="background:rgba(139,92,246,0.25);border:1px solid rgba(139,92,246,0.5);border-radius:6px;padding:5px 10px;font-size:0.76em;color:#c4b5fd;white-space:nowrap;font-family:'Orbitron',sans-serif;">Ch 8</div>
  <div style="background:rgba(59,130,246,0.25);border:1px solid rgba(59,130,246,0.5);border-radius:6px;padding:5px 10px;font-size:0.76em;color:#93c5fd;white-space:nowrap;font-family:'Orbitron',sans-serif;">Vs 4</div>
  <div style="background:rgba(239,68,68,0.25);border:1px solid rgba(239,68,68,0.5);border-radius:6px;padding:5px 10px;font-size:0.76em;color:#fca5a5;white-space:nowrap;font-family:'Orbitron',sans-serif;">End 2</div>
</div>
<p style="margin:0;font-size:0.82em;opacity:0.6;">The playhead highlights each section in real time.</p>
`;

const SAVE_SHARE_HTML = `
<div style="display:flex;flex-direction:column;gap:11px;margin-bottom:14px;">
  <div style="display:flex;align-items:flex-start;gap:10px;">
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 384 512" style="width:13px;height:13px;fill:#7fd3ff;flex-shrink:0;margin-top:3px;"><path d="M0 48C0 21.5 21.5 0 48 0h288c26.5 0 48 21.5 48 48V512L192 400 0 512V48z"/></svg>
    <div>
      <div style="font-weight:600;font-size:0.9em;">Save loops</div>
      <div style="font-size:0.8em;opacity:0.6;">Keep the ones you like in your personal library</div>
    </div>
  </div>
  <div style="display:flex;align-items:flex-start;gap:10px;">
    <span style="font-size:1.15em;flex-shrink:0;line-height:1.5;">🔗</span>
    <div>
      <div style="font-weight:600;font-size:0.9em;">Share any loop</div>
      <div style="font-size:0.8em;opacity:0.6;">Send a direct link to your bandmates</div>
    </div>
  </div>
</div>
<p style="margin:0;font-size:0.82em;opacity:0.5;border-top:1px solid rgba(128,128,128,0.15);padding-top:10px;">
  Log in to access your full library.
</p>
`;

const COMMUNITY_HTML = `
<p style="margin:0 0 12px;">Log in to unlock:</p>
<div style="display:flex;flex-direction:column;gap:10px;">
  <div style="display:flex;align-items:flex-start;gap:10px;">
    <div style="display:flex;align-items:center;justify-content:center;width:34px;height:34px;background:rgba(127,211,255,0.08);border:1px solid rgba(127,211,255,0.18);border-radius:10px;flex-shrink:0;font-size:1.1em;">🎵</div>
    <div>
      <div style="font-weight:600;font-size:0.88em;">My Loops</div>
      <div style="font-size:0.78em;opacity:0.6;">Your saved loop library</div>
    </div>
  </div>
  <div style="display:flex;align-items:flex-start;gap:10px;">
    <div style="display:flex;align-items:center;justify-content:center;width:34px;height:34px;background:rgba(157,255,179,0.08);border:1px solid rgba(157,255,179,0.18);border-radius:10px;flex-shrink:0;font-size:1.1em;">💿</div>
    <div>
      <div style="font-weight:600;font-size:0.88em;">My Albums</div>
      <div style="font-size:0.78em;opacity:0.6;">Generate loops from your uploaded albums</div>
    </div>
  </div>
  <div style="display:flex;align-items:flex-start;gap:10px;">
    <div style="display:flex;align-items:center;justify-content:center;width:34px;height:34px;background:rgba(200,180,255,0.08);border:1px solid rgba(200,180,255,0.18);border-radius:10px;flex-shrink:0;font-size:1.1em;">☁️</div>
    <div>
      <div style="font-weight:600;font-size:0.88em;">Upload</div>
      <div style="font-size:0.78em;opacity:0.6;">Upload your own loop samples — musicians worldwide jam over them and fuel the community pool</div>
    </div>
  </div>
</div>
`;

export function initOnboardingTour(): void {
    const isMobile = window.innerWidth <= 480;

    const driverObj = driver({
        animate: true,
        overlayOpacity: 0.65,
        allowClose: true,
        doneBtnText: "Let's go!",
        nextBtnText: 'Next',
        prevBtnText: 'Back',
        stagePadding: isMobile ? 4 : 8,
        popoverOffset: isMobile ? 8 : 12,
        onPopoverRender: (_popover, { state }) => {
            if (state.activeIndex === 0) {
                const btn = document.querySelector('.driver-popover-next-btn') as HTMLElement | null;
                if (btn) btn.textContent = 'Show me how →';
            }
        },
        onDestroyed: () => {
            const checkbox = document.getElementById('tourDontShowCheckbox') as HTMLInputElement | null;
            if (checkbox && checkbox.checked) {
                localStorage.setItem(TOUR_DONE_KEY, '1');
            }
        },
        steps: [
            {
                popover: {
                    title: 'l∞perc',
                    description: LANDING_HTML,
                    side: 'over',
                    align: 'center',
                    popoverClass: 'tour-landing',
                },
            },
            {
                element: '#lcdSection',
                popover: {
                    title: 'Pick Your Vibe',
                    description: STYLE_GROUPS_HTML,
                    side: 'bottom',
                    align: 'center',
                },
            },
            {
                element: '#pedalBtnGroup',
                popover: {
                    title: 'Loop Complexity',
                    description: COMPLEXITY_HTML,
                    side: 'bottom',
                    align: 'center',
                },
            },
            {
                element: '#tempoControls',
                popover: {
                    title: 'Real-Time Tempo',
                    description: '− BPM and + BPM adjust speed on the fly.<br><br>No pitch artifacts. No restarts.<br><br>Match your instrument, your teacher\'s track, or wherever your hands naturally land.',
                    side: 'top',
                    align: 'center',
                },
            },
            {
                popover: {
                    title: 'See the Structure',
                    description: `After generating, a visual timeline shows your loop structure.${SEQUENCE_STRIP_HTML}Know exactly where you are in the song.`,
                    side: 'over',
                    align: 'center',
                },
            },
            {
                element: '#saveBtn',
                popover: {
                    title: 'Save. Share.',
                    description: SAVE_SHARE_HTML,
                    side: 'top',
                    align: 'center',
                },
            },
            {
                element: '#loginBtn',
                popover: {
                    title: 'Where It Gets Interesting',
                    description: COMMUNITY_HTML,
                    side: 'bottom',
                    align: 'end',
                },
            },
            {
                element: '#generateBtn',
                disableActiveInteraction: true,
                popover: {
                    title: 'Ready to Jam?',
                    description: 'Hit <strong>Generate</strong> — get a loop in under a second.<br><br>Your jam session starts now.',
                    side: 'top',
                    align: 'center',
                },
            },
        ],
    });

    if (!localStorage.getItem(TOUR_DONE_KEY)) {
        setTimeout(() => driverObj.drive(), 800);
    }

    const helpBtn = document.getElementById('tourHelpBtn');
    if (helpBtn) {
        helpBtn.addEventListener('click', () => driverObj.drive());
    }
}
